import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import { Plus, Check, X, Trash2, ChevronDown, ChevronUp, Upload, Hammer, FolderKanban } from 'lucide-react';
import { MediaViewer } from '../components/shared/MediaComponents';

/* EXP14 helpers */
const fwoItems = (o) => (Array.isArray(o.items) && o.items.length ? o.items.map((i) => ({ name: i.name || i.material || i.title || '', qty: i.qty ?? i.quantity ?? '', size: i.size || i.measurements || '' })) : [{ name: o.title, qty: o.quantity, size: o.measurements }]);
const fwoWork = (o) => {
  if (o.approval_status === 'rejected') return { label: 'Rejected', cls: 'bg-red-100 text-red-700' };
  if (o.approval_status && o.approval_status !== 'approved') return { label: 'Waiting approval', cls: 'bg-amber-100 text-amber-700' };
  if (o.dispatched_at) return { label: 'Dispatched', cls: 'bg-purple-100 text-purple-700' };
  if (o.status === 'completed') return { label: 'Completed · ready', cls: 'bg-green-100 text-green-700' };
  return { label: 'In factory', cls: 'bg-blue-100 text-blue-700' };
};
const BLANK_ITEM = { name: '', qty: '', size: '', files: [] };
/* EXP18: photos per item */
const ItemPhotos = ({ o, i }) => { const m = (o.item_media || {})[String(i)] || []; return m.length ? <div className="mt-1 mb-1"><MediaViewer attachments={m} /></div> : null; };

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'pending_admin', label: 'Pending Admin' },
  { key: 'pending_md', label: 'Pending MD' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

export default function FactoryWorkOrders() {
  const { user } = useAuth();
  const role = user?.role;
  const isApprover = ['admin', 'md'].includes(role); /* EXP17 approval inbox */
  const [mainTab, setMainTab] = useState(isApprover ? 'approve' : 'all');
  const [pending, setPending] = useState([]); const [pLoading, setPLoading] = useState(true); const [busyId, setBusyId] = useState(null);
  useEffect(() => { if (isApprover) setMainTab('approve'); }, [isApprover]);
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const [useCustomProject, setUseCustomProject] = useState(false);
  const [workStatus, setWorkStatus] = useState('active');
  const [workOrders, setWorkOrders] = useState([]);

  const [form, setForm] = useState({
    project_id: '', custom_project_name: '', title: '', description: '',
    quantity: '', measurements: '', deadline: '', work_order_id: '',
  });
  const [files, setFiles] = useState([]);
  const [items, setItems] = useState([{ ...BLANK_ITEM }]); const [priority, setPriority] = useState('normal'); /* EXP14 */
  const setItem = (i, k, v) => setItems((p) => p.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const fetchData = () => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('approval_status', statusFilter);
    api.get(`/factory/work-orders/all-requests?${params}`).then(r => {
      setOrders(r.data.orders || []);
      setCounts(r.data.counts || {});
      try { window.dispatchEvent(new Event('pms-refresh-badges')); } catch (e) {} /* EXP19 menu badge in sync */
    }).catch(() => {});
    if (['admin', 'md'].includes(role)) {
      api.get('/factory/work-orders/all-requests').then(r => { setPending((r.data.orders || []).filter(o => ['pending_admin', 'pending_md'].includes(o.approval_status))); setPLoading(false); })
        .catch(() => { setPLoading(false); toast.error('Could not load approvals — check internet'); });
    }
  };
  const inboxAct = async (o, kind) => {
    if (busyId) return;
    let body;
    if (kind === 'reject') { const reason = window.prompt('Reason for rejecting ' + (o.wo_number || 'this work order') + ' (optional):', ''); if (reason === null) return; body = { reason }; }
    setBusyId(o.id);
    try {
      const path = kind === 'admin' ? 'approve-admin' : kind === 'md' ? 'approve-md' : 'reject';
      await api.put(`/factory/work-orders/${o.id}/${path}`, body);
      toast.success(kind === 'reject' ? 'Rejected' : kind === 'admin' ? 'Approved (1/2) — sent to MD' : 'Approved — sent to factory');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setBusyId(null);
  };
  const fmtD = (d) => { try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); } catch (e) { return ''; } };

  useEffect(() => { fetchData(); }, [statusFilter]);
  useEffect(() => { api.get('/project-options').then(r => setProjects(r.data.projects || [])) /* AVB4-fe */.catch(() => {}); }, []);
  useEffect(() => {
    if (form.project_id) {
      api.get('/work-orders?project_id=' + form.project_id).then(r => setWorkOrders(r.data.workOrders || [])).catch(() => setWorkOrders([]));
    } else { setWorkOrders([]); }
  }, [form.project_id]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const validItems = items.filter((i) => i.name.trim()); // EXP14: several items, WO link optional
    if (!useCustomProject && !form.project_id) { toast.error('Select a project or use custom name'); return; }
    if (useCustomProject && !form.custom_project_name.trim()) { toast.error('Enter a project/site name'); return; }
    if (!validItems.length) { toast.error('Add at least one item'); return; }
    setLoading(true);
    try {
      const payload = {
        items: validItems.map((i) => ({ name: i.name.trim(), qty: parseFloat(i.qty) || 1, size: i.size.trim() })),
        priority,
        description: form.description,
        deadline: form.deadline || null,
      };
      if (useCustomProject) {
        payload.custom_project_name = form.custom_project_name.trim();
        payload.project_id = null;
      } else {
        payload.project_id = form.project_id;
        if (form.work_order_id) payload.work_order_id = form.work_order_id;
      }

      const res = await api.post('/factory/work-orders/request', payload);
      const orderId = res.data.order?.id;
      if (orderId && files.length > 0) {
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('entity_type', 'factory_work_order');
          fd.append('entity_id', orderId);
          try { await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch {}
        }
      }
      if (orderId) { // EXP18: item photos -> entity 'factory_wo_item', id '<order>.<itemIndex>'
        let bad = 0;
        for (let k = 0; k < validItems.length; k++) for (const file of (validItems[k].files || [])) {
          const fd = new FormData(); fd.append('file', file); fd.append('entity_type', 'factory_wo_item'); fd.append('entity_id', orderId + '.' + k);
          try { await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch { bad++; }
        }
        if (bad) toast.error(bad + ' item photo(s) failed to upload');
      }
      toast.success(`Work Order ${res.data.wo_number} submitted for approval`);
      setForm({ project_id: '', custom_project_name: '', title: '', description: '', quantity: '', measurements: '', deadline: '', work_order_id: '' });
      setFiles([]); setItems([{ ...BLANK_ITEM }]); setPriority('normal');
      setShowCreate(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setLoading(false);
  };

  const approveAdmin = async (id) => {
    try { await api.put(`/factory/work-orders/${id}/approve-admin`); toast.success('Admin approved'); fetchData(); } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };
  const approveMD = async (id) => {
    try { await api.put(`/factory/work-orders/${id}/approve-md`); toast.success('MD approved — sent to Factory'); fetchData(); } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };
  const reject = async (id) => {
    try { await api.put(`/factory/work-orders/${id}/reject`, { reason: rejectReason }); toast.success('Rejected'); setRejectId(null); setRejectReason(''); fetchData(); } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this work order?')) return;
    try { await api.delete(`/factory/work-orders/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const handleFileUpload = async (orderId, fileList) => {
    setUploadingId(orderId);
    try {
      for (const file of fileList) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entity_type', 'factory_work_order');
        fd.append('entity_id', orderId);
        await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('File uploaded');
      fetchData();
    } catch { toast.error('Upload failed'); }
    setUploadingId(null);
  };

  const approvalBadge = (s) => {
    const map = {
      pending_admin: 'bg-amber-100 text-amber-700',
      pending_md: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return map[s] || 'bg-gray-100 text-gray-600';
  };
  const approvalLabel = (s) => {
    const map = { pending_admin: 'Pending Admin', pending_md: 'Pending MD', approved: 'Approved', rejected: 'Rejected' };
    return map[s] || s;
  };

  const totalAll = Object.values(counts).reduce((s, v) => s + v, 0);
  const completedCount = orders.filter(o => o.status === 'completed').length;
  const displayOrders = workStatus === 'completed'
    ? orders.filter(o => o.status === 'completed')
    : orders.filter(o => o.status !== 'completed');
  const handleWorkStatus = (ws) => { setWorkStatus(ws); if (ws === 'completed') setStatusFilter(''); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Factory Work Orders</h1>
          <p className="text-sm text-gray-500">Factory work order requests with approval</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1.5 px-3 py-2 bg-brand-600 text-white rounded-xl text-xs font-medium">
          <Plus size={14} /> New Work Order
        </button>
      </div>

      {isApprover && (
        <div className="flex gap-2">
          <button onClick={() => setMainTab('approve')} className={`px-4 py-2 rounded-full text-xs font-bold ${mainTab === 'approve' ? 'bg-amber-500 text-white' : 'bg-white border text-gray-600'}`}>Awaiting approval ({pending.length})</button>
          <button onClick={() => setMainTab('all')} className={`px-4 py-2 rounded-full text-xs font-bold ${mainTab === 'all' ? 'bg-brand-600 text-white' : 'bg-white border text-gray-600'}`}>All work orders</button>
        </div>
      )}
      {isApprover && mainTab === 'approve' && (
        <div className="space-y-3 max-w-3xl">
          {pLoading ? <p className="text-center text-gray-400 text-sm py-8">Loading…</p> : pending.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border"><p className="text-sm text-gray-400">No work orders waiting for approval 🎉</p><button onClick={() => setMainTab('all')} className="mt-3 text-xs text-brand-600 font-semibold underline">View all work orders</button></div>
          ) : pending.map(o => (
            <div key={o.id} className={'bg-white rounded-xl border p-4 shadow-sm ' + (o.priority === 'urgent' ? 'border-red-300' : '')}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold text-gray-900">{o.creator?.name || 'Supervisor'}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1"><FolderKanban size={11} /> {o.project?.name || o.custom_project_name || '—'}{o.project?.location ? ' · ' + o.project.location : ''}</p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${o.approval_status === 'pending_admin' ? 'bg-amber-100 text-amber-700' : 'bg-orange-100 text-orange-700'}`}>{o.approval_status === 'pending_admin' ? 'Pending 1st approval' : 'Pending 2nd approval'}</span>
                  {o.priority === 'urgent' && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-red-600 text-white">🔥 Urgent</span>}
                </div>
              </div>
              <div className="mt-2 bg-gray-50 rounded-lg px-3 py-2 space-y-1">{fwoItems(o).map((it, i) => <div key={i}><p className="text-sm"><span className="font-semibold">{it.name}</span><span className="text-gray-500">{it.qty ? ` · Qty ${it.qty}` : ''}{it.size ? ` · ${it.size}` : ''}</span></p><ItemPhotos o={o} i={i} /></div>)}</div>
              {o.description && <p className="text-xs text-gray-600 mt-2 whitespace-pre-line">{o.description}</p>}
              <p className="text-[10px] text-gray-400 mt-1.5">{o.wo_number}{o.deadline ? ' · Required by ' + fmtD(o.deadline) : ''} · requested {fmtD(o.created_at || o.createdAt)}</p>
              {(o.media || []).length > 0 && <div className="mt-2"><MediaViewer attachments={o.media} /></div>}
              <div className="flex gap-2 mt-3">
                {o.approval_status === 'pending_admin' && <button onClick={() => inboxAct(o, 'admin')} disabled={busyId === o.id} className="flex-1 py-2.5 bg-green-600 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-50"><Check size={15} /> Approve 1/2</button>}
                {o.approval_status === 'pending_md' && <button onClick={() => inboxAct(o, 'md')} disabled={busyId === o.id} className="flex-1 py-2.5 bg-green-700 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-50"><Check size={15} /> Approve 2/2</button>}
                <button onClick={() => inboxAct(o, 'reject')} disabled={busyId === o.id} className="flex-1 py-2.5 bg-red-500 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-50"><X size={15} /> Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {mainTab === 'all' && (<>
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border p-3 text-center">
          <p className="text-[10px] text-gray-500 uppercase font-bold">Total</p>
          <p className="text-xl font-bold text-brand-600">{totalAll}</p>
        </div>
        <div className="bg-white rounded-xl border p-3 text-center">
          <p className="text-[10px] text-gray-500 uppercase font-bold">Pending</p>
          <p className="text-xl font-bold text-amber-600">{(counts.pending_admin || 0) + (counts.pending_md || 0)}</p>
        </div>
        <div className="bg-white rounded-xl border p-3 text-center">
          <p className="text-[10px] text-gray-500 uppercase font-bold">Approved</p>
          <p className="text-xl font-bold text-green-600">{counts.approved || 0}</p>
        </div>
        <div className="bg-white rounded-xl border p-3 text-center">
          <p className="text-[10px] text-gray-500 uppercase font-bold">Rejected</p>
          <p className="text-xl font-bold text-red-600">{counts.rejected || 0}</p>
        </div>
      </div>

      </>)}
      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border p-4 space-y-3">
          <p className="text-sm font-bold text-gray-700 uppercase">New Work Order Request</p>

          <div className="flex items-center gap-3">
            <button type="button" onClick={() => { setUseCustomProject(false); setForm(f => ({ ...f, custom_project_name: '' })); }}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition ${!useCustomProject ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
              🏗️ Select Project
            </button>
            <button type="button" onClick={() => { setUseCustomProject(true); setForm(f => ({ ...f, project_id: '' })); }}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition ${useCustomProject ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
              ✏️ Custom Name
            </button>
          </div>

          {!useCustomProject ? (
            <select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}
              required className="w-full px-3 py-2.5 border rounded-lg text-sm bg-white">
              <option value="">Select project / site *</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name} — {p.location}</option>)}
            </select>
          ) : (
            <input value={form.custom_project_name} onChange={e => setForm(f => ({ ...f, custom_project_name: e.target.value }))}
              placeholder="Custom project / site name *" required className="w-full px-3 py-2.5 border rounded-lg text-sm" />
          )}

          {/* Work Order (optional) — only when project is selected */}
          {!useCustomProject && form.project_id && workOrders.length > 0 && (
            <select value={form.work_order_id} onChange={e => setForm(f => ({ ...f, work_order_id: e.target.value }))}
              className="w-full px-3 py-2.5 border rounded-lg text-sm bg-white">
              <option value="">Link to site work order (optional)</option>
              {workOrders.map(wo => <option key={wo.id} value={wo.id}>{wo.wo_number} — {wo.title}</option>)}
            </select>
          )}

          {/* EXP27: each item its own card with a big per-item file button + multiple files */}
          <div className="space-y-3">
            <p className="text-xs text-gray-500 font-bold uppercase">Items to make *</p>
            {items.map((it, i) => (
              <div key={i} className="border rounded-xl p-3 bg-gray-50 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-brand-700">Item {i + 1}</span>
                  {items.length > 1 && <button type="button" onClick={() => setItems(p => p.filter((_, j) => j !== i))} className="flex items-center gap-1 text-xs text-red-500 font-semibold"><X size={14} /> Remove</button>}
                </div>
                <input value={it.name} onChange={e => setItem(i, 'name', e.target.value)} placeholder="Item (e.g. Wardrobe, TV unit)" className="w-full px-3 py-2.5 border rounded-lg text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" inputMode="decimal" value={it.qty} onChange={e => setItem(i, 'qty', e.target.value)} placeholder="Qty" className="w-full px-3 py-2.5 border rounded-lg text-sm" />
                  <input value={it.size} onChange={e => setItem(i, 'size', e.target.value)} placeholder="Size L×W×H" className="w-full px-3 py-2.5 border rounded-lg text-sm" />
                </div>
                <label className="flex items-center justify-center gap-2 w-full py-2.5 border-2 border-dashed border-brand-300 rounded-lg text-sm font-semibold text-brand-700 bg-brand-50 cursor-pointer">
                  <Upload size={16} /> Add drawings / photos / PDF{(it.files || []).length ? ' (' + it.files.length + ')' : ''}
                  <input type="file" multiple accept="image/*,.pdf" className="hidden" onChange={e => { const fl = Array.from(e.target.files || []); e.target.value = ''; setItem(i, 'files', [...(it.files || []), ...fl]); }} />
                </label>
                {(it.files || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {it.files.map((f, fi) => (
                      <span key={fi} className="inline-flex items-center gap-1 bg-white border rounded-lg px-2 py-1 text-xs text-gray-700 max-w-[200px]">
                        <span className="truncate">{f.name}</span>
                        <button type="button" onClick={() => setItem(i, 'files', it.files.filter((_, j) => j !== fi))} className="text-red-400 shrink-0"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setItems(p => [...p, { ...BLANK_ITEM }])} className="w-full py-2.5 border-2 border-dashed border-brand-300 rounded-xl text-sm font-bold text-brand-600 flex items-center justify-center gap-1"><Plus size={16} /> Add another item</button>
          </div>

          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Notes for factory — material, finish, colour, design details..." rows={2}
            className="w-full px-3 py-2.5 border rounded-lg text-sm" />

          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-gray-500 font-bold uppercase">Required by
              <input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} className="mt-1 w-full px-3 py-2.5 border rounded-lg text-sm font-normal text-gray-800" />
            </label>
            <div className="text-xs text-gray-500 font-bold uppercase">Priority
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                <button type="button" onClick={() => setPriority('normal')} className={`py-2.5 rounded-lg text-sm font-semibold border ${priority === 'normal' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600'}`}>Normal</button>
                <button type="button" onClick={() => setPriority('urgent')} className={`py-2.5 rounded-lg text-sm font-semibold border ${priority === 'urgent' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-600 border-red-200'}`}>🔥 Urgent</button>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 font-bold uppercase mb-1">📎 Reference images / drawings</p>
            <input type="file" accept="image/*,video/*,.pdf,.doc,.docx" multiple
              onChange={e => setFiles(Array.from(e.target.files))}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-brand-50 file:text-brand-700" />
            {files.length > 0 && <p className="text-xs text-green-600 mt-1">📎 {files.length} file(s) selected</p>}
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => setShowCreate(false)} className="flex-1 py-2.5 border rounded-xl text-sm">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium disabled:opacity-50">
              {loading ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </form>
      )}

      {mainTab === 'all' && (<>
      {/* Work Status toggle */}
      <div className="flex gap-2">
        <button onClick={() => handleWorkStatus('active')}
          className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide ${workStatus === 'active' ? 'bg-brand-600 text-white' : 'bg-white text-gray-500 border'}`}>
          Active
        </button>
        <button onClick={() => handleWorkStatus('completed')}
          className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide ${workStatus === 'completed' ? 'bg-green-600 text-white' : 'bg-white text-gray-500 border'}`}>
          Completed ({completedCount})
        </button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 overflow-x-auto border-b pb-px scrollbar-hide">
        {STATUS_TABS.map(t => (
          <button key={t.key} onClick={() => setStatusFilter(t.key)}
            className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition ${statusFilter === t.key ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500'}`}>
            {t.label} {t.key ? `(${counts[t.key] || 0})` : `(${totalAll})`}
          </button>
        ))}
      </div>

      {/* Orders list */}
      <div className="space-y-3">
        {displayOrders.map(o => {
          const expanded = expandedId === o.id;
          const siteName = o.project?.name || o.custom_project_name || 'No site';
          return (
            <div key={o.id} className="bg-white rounded-xl border overflow-hidden">
              <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expanded ? null : o.id)}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {o.wo_number && <span className="text-xs font-mono text-gray-400">{o.wo_number}</span>}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${approvalBadge(o.approval_status)}`}>
                        {approvalLabel(o.approval_status)}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${fwoWork(o).cls}`}>{fwoWork(o).label}</span>
                      {o.priority === 'urgent' && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-red-600 text-white">🔥 Urgent</span>}
                    </div>
                    <div className="mt-1 space-y-0.5">{fwoItems(o).map((it, i) => <div key={i}><p className="font-semibold text-sm">{it.name}<span className="font-normal text-gray-500">{it.qty ? ` · Qty ${it.qty}` : ''}{it.size ? ` · ${it.size}` : ''}</span></p><ItemPhotos o={o} i={i} /></div>)}</div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      <span className="inline-flex items-center gap-1"><FolderKanban size={10} /> {siteName}</span>
                      {' · '}{o.creator?.name}
                      {o.deadline && ` · Due: ${o.deadline}`}
                    </p>
                  </div>
                  <div className="ml-3 flex items-center gap-1">
                    {o.progress_percentage > 0 && <span className="text-sm font-bold text-brand-600">{o.progress_percentage}%</span>}
                    {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                  </div>
                </div>
              </div>

              {expanded && (
                <div className="px-4 pb-4 border-t pt-3 space-y-3">
                  {o.description && <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-2">{o.description}</p>}

                  {/* Approval trail */}
                  <div className="text-xs text-gray-500 space-y-1">
                    {o.admin_approved_by && <p>✓ Admin approved {o.admin_approved_at ? new Date(o.admin_approved_at).toLocaleDateString() : ''}</p>}
                    {o.md_approved_by && <p>✓ MD approved {o.md_approved_at ? new Date(o.md_approved_at).toLocaleDateString() : ''}</p>}
                    {o.rejection_reason && <p className="text-red-600">Reason: {o.rejection_reason}</p>}
                    {o.completed_at && <p className="text-green-700">✓ Completed at factory {new Date(o.completed_at).toLocaleDateString('en-IN')}</p>}
                    {o.dispatched_at && <p className="text-purple-700">🚚 Dispatched {new Date(o.dispatched_at).toLocaleDateString('en-IN')}{o.dispatch_note ? ' — ' + o.dispatch_note : ''}</p>}
                  </div>

                  {/* Media */}
                  {o.media && o.media.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase mb-1">Reference Files</p>
                      <MediaViewer attachments={o.media} />
                    </div>
                  )}

                  {/* Upload */}
                  <label className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs cursor-pointer transition w-fit">
                    <Upload size={12} /> {uploadingId === o.id ? 'Uploading...' : 'Attach File'}
                    <input type="file" accept="image/*,video/*,.pdf" multiple className="hidden"
                      onChange={e => { if (e.target.files.length) handleFileUpload(o.id, Array.from(e.target.files)); }} />
                  </label>

                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    {['admin', 'md'].includes(role) && o.approval_status === 'pending_admin' && (
                      <button onClick={() => approveAdmin(o.id)} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium">
                        <Check size={12} /> Approve 1/2
                      </button>
                    )}
                    {['admin', 'md'].includes(role) && o.approval_status === 'pending_md' && (
                      <button onClick={() => approveMD(o.id)} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium">
                        <Check size={12} /> Approve 2/2
                      </button>
                    )}
                    {['admin', 'md'].includes(role) && ['pending_admin', 'pending_md'].includes(o.approval_status) && (
                      rejectId === o.id ? (
                        <div className="flex items-center gap-2">
                          <input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason..." className="px-2 py-1.5 border rounded-lg text-xs w-40" />
                          <button onClick={() => reject(o.id)} className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs">Reject</button>
                          <button onClick={() => { setRejectId(null); setRejectReason(''); }} className="px-2 py-1.5 text-gray-500 text-xs">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setRejectId(o.id)} className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium">
                          <X size={12} /> Reject
                        </button>
                      )
                    )}
                    {(role === 'admin' || role === 'md') && (
                      <button onClick={() => remove(o.id)} className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs">
                        <Trash2 size={12} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {displayOrders.length === 0 && <p className="text-center text-gray-400 text-sm py-8">{workStatus === 'completed' ? 'No completed work orders' : 'No work orders found'}</p>}
      </div>
      </>)}
    </div>
  );
}
